/**
 * Events → Coverage ("כיסוי ארצי") — national coverage module view.
 *
 * Composes the live `/v1/contract/coverage` envelope with the honesty
 * primitives (`<Freshness/>`, `<Denominator/>`, `<AnomalyChip/>`) and
 * the new interactive `<IsraelMap/>` (real geographic map with one dot
 * per district). The previous tile-cartogram chart still lives in
 * `components/charts/TileCartogram.tsx` for other modules that may
 * adopt it, but Coverage now leads with the geographic view + a
 * sticky data card on the side.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { Suite, ModuleSpec } from '../../../data/types/platform';
import { useEventsCoverage } from '../../../data/queries/useEventsCoverage';
import {
  IsraelMap,
  DISTRICT_COORDS,
  ISRAEL_FALLBACK,
  type IsraelMapDistrict,
} from '../../../components/charts/IsraelMap';
import { AnomalyChip, Denominator, Freshness } from '../../../components/honesty';
import { Icon } from '../../../components/ui/Icon';
import { formatNumber, formatPercent } from '../../../utils/format';

export interface CoverageViewProps {
  system?: Suite;
  module?: ModuleSpec;
}

type CoverageMetric = 'attended' | 'events' | 'reached';

const METRIC_OPTIONS: Array<{ id: CoverageMetric; label: string }> = [
  { id: 'attended', label: 'הגיעו' },
  { id: 'events',   label: 'אירועים' },
  { id: 'reached',  label: 'פניות' },
];

function toEpoch(iso: string): number {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : Date.now();
}

export function CoverageView({ system, module }: CoverageViewProps) {
  const { data, isLoading, error } = useEventsCoverage();

  return (
    <article className="module-view coverage-view module-page">
      <header className="mp-head">
        <div className="mp-eyebrow">
          <span>{system?.en ?? 'EVENTS'}</span>
          <span className="mp-sep">/</span>
          <span>{module?.he ?? 'כיסוי ארצי'}</span>
        </div>
        <h1>{module?.he ?? 'כיסוי ארצי'}</h1>
        <p className="mp-sub">
          {module?.sub ?? 'צפיפות אירועים והגעות לפי מחוז — מפה גיאוגרפית אינטראקטיבית של ישראל.'}
        </p>
        <div className="mp-meta">
          {data && (
            <>
              <Freshness updatedAt={toEpoch(data.source_pulled_at)} />
              <span className="mp-dot" />
              <span>scope: {data.scope}</span>
              {data.meta.mock && (
                <>
                  <span className="mp-dot" />
                  <span>mock fixtures</span>
                </>
              )}
            </>
          )}
        </div>
      </header>

      <div className="mp-body">
        {isLoading && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="loader" size={20} />
                <span>טוען נתוני כיסוי…</span>
              </div>
            </div>
          </div>
        )}

        {error && (
          <div className="mp-panel">
            <div className="mp-panel-body">
              <div className="mp-placeholder">
                <Icon name="alert-triangle" size={20} />
                <span>שגיאה בטעינת המודול: {error.message}</span>
              </div>
            </div>
          </div>
        )}

        {data && <CoverageBody envelope={data} />}
      </div>
    </article>
  );
}

interface CoverageBodyProps {
  envelope: NonNullable<ReturnType<typeof useEventsCoverage>['data']>;
}

function CoverageBody({ envelope }: CoverageBodyProps) {
  const { districts, totals } = envelope.data;
  const [metric, setMetric] = useState<CoverageMetric>('attended');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const navigate = useNavigate();

  const selectedRow = selectedId
    ? districts.find((d) => d.id === selectedId) ?? null
    : null;

  /* ---- KPI scope ---- */
  const scope = selectedRow
    ? {
        label: selectedRow.he,
        events: selectedRow.events,
        reached: selectedRow.reached,
        rsvp_yes: selectedRow.rsvp_yes,
        attended: selectedRow.attended,
        walk_in: selectedRow.walk_in,
      }
    : {
        label: 'ארצי',
        events: totals.events,
        reached: totals.reached,
        rsvp_yes: totals.rsvp_yes,
        attended: totals.attended,
        walk_in: totals.walk_in,
      };

  const activeDistricts = useMemo(
    () => districts.filter((d) => d.events > 0).length,
    [districts],
  );
  const topDistrict = useMemo(() => {
    if (districts.length === 0) return null;
    return [...districts].sort((a, b) => b.attended - a.attended)[0];
  }, [districts]);

  const yesAtt = Math.max(0, scope.attended - scope.walk_in);
  const yesShow = scope.rsvp_yes > 0 ? yesAtt / scope.rsvp_yes : 0;
  const walkInShare = scope.attended > 0 ? scope.walk_in / scope.attended : 0;
  const attendanceRecorded = Math.round(totals.records * envelope.recorded.attendance);

  /* ---- Map markers — fold API rows through DISTRICT_COORDS. ---- */
  const mapDistricts: IsraelMapDistrict[] = useMemo(
    () =>
      districts.map((d) => {
        const coords = DISTRICT_COORDS[d.he];
        const [lat, lng] = coords ?? ISRAEL_FALLBACK;
        const value =
          metric === 'events'  ? d.events  :
          metric === 'reached' ? d.reached :
                                 d.attended;
        return {
          id: d.id,
          he: d.he,
          lat,
          lng,
          value,
          events: d.events,
          reached: d.reached,
          rsvp_yes: d.rsvp_yes,
          attended: d.attended,
          walk_in: d.walk_in,
          unlocated: !coords,
        };
      }),
    [districts, metric],
  );

  return (
    <>
      {/* KPI row — district-scoped when a marker is selected, else national. */}
      <div className="kpi-grid cols-4">
        <KpiCell
          label="אירועים"
          value={formatNumber(scope.events)}
          sub={`${scope.label} · ${activeDistricts}/${districts.length} מחוזות פעילים`}
        />
        <KpiCell
          label="הגיעו בפועל"
          value={formatNumber(scope.attended)}
          sub={`${formatNumber(yesAtt)} אישרו מראש · ${formatNumber(scope.walk_in)} walk-in`}
          accent
        />
        <KpiCell
          label="המרת אישור←הגעה"
          value={`${Math.round(yesShow * 100)}%`}
          sub={`על בסיס ${formatPercent(envelope.recorded.attendance, 0)} רישום הגעה`}
          focus
        />
        <KpiCell
          label="מחוז מוביל"
          value={topDistrict ? topDistrict.he : '—'}
          sub={
            topDistrict
              ? `${formatNumber(topDistrict.attended)} הגעות · ${formatNumber(topDistrict.events)} אירועים`
              : 'אין נתונים'
          }
        />
      </div>

      {/* Map + sticky district data card. */}
      <div className="panel">
        <div
          className="panel-head"
          style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}
        >
          <div className="title">פעילות לפי מחוז</div>
          <div className="sub" style={{ flex: 1 }}>
            {selectedRow
              ? `נבחר: ${selectedRow.he} — לחץ שוב לביטול`
              : 'לחץ על נקודה במפה לסינון לפי מחוז'}
          </div>
          <div
            className="actions"
            style={{ display: 'inline-flex', gap: 6, flexWrap: 'wrap' }}
          >
            {METRIC_OPTIONS.map((opt) => {
              const active = opt.id === metric;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setMetric(opt.id)}
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 12,
                    padding: '6px 12px',
                    background: active ? 'var(--ink)' : 'var(--surface)',
                    color: active ? 'var(--paper)' : 'var(--ink)',
                    border: '1px solid ' + (active ? 'var(--ink)' : 'var(--hairline)'),
                    borderRadius: 4,
                    cursor: 'pointer',
                  }}
                >
                  גודל לפי {opt.label}
                </button>
              );
            })}
            {selectedRow && (
              <button
                type="button"
                onClick={() => setSelectedId(null)}
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontSize: 12,
                  padding: '6px 12px',
                  background: 'var(--surface)',
                  color: 'var(--ink)',
                  border: '1px solid var(--hairline)',
                  borderRadius: 4,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <Icon name="arrow-left" size={12} />
                חזרה למבט ארצי
              </button>
            )}
          </div>
        </div>
        <div
          style={{
            display: 'flex',
            gap: 16,
            padding: '12px 12px 16px',
            alignItems: 'flex-start',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '1 1 60%', minWidth: 320 }}>
            <IsraelMap
              districts={mapDistricts}
              selectedId={selectedId}
              onSelect={setSelectedId}
              metric={metric}
            />
          </div>
          <div style={{ flex: '1 1 32%', minWidth: 260, position: 'sticky', top: 16 }}>
            <DistrictCard
              scope={scope}
              totals={totals}
              isNational={!selectedRow}
              trend={selectedRow?.trend ?? null}
              onOpenFunnel={() =>
                navigate(
                  selectedRow
                    ? `/system/events/funnel?district=${encodeURIComponent(selectedRow.id)}`
                    : '/system/events/funnel',
                )
              }
            />
          </div>
        </div>
      </div>

      {/* District leaderboard table. */}
      <DistrictLeaderboard
        rows={districts}
        selectedId={selectedId}
        onSelect={(id) => setSelectedId((prev) => (prev === id ? null : id))}
      />

      {/* Honesty footer. */}
      <footer
        className="mv-foot"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 12,
          padding: '16px 0 4px',
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          color: 'var(--ink-3)',
          alignItems: 'center',
        }}
      >
        <Denominator n={totals.records} total={totals.records} label="רשומות השתתפות" />
        <Denominator n={attendanceRecorded} total={totals.records} label="עם רישום הגעה" />
        <span style={{ color: 'var(--ink-3)' }}>
          walk-in: {Math.round(walkInShare * 100)}% מהמגיעים
        </span>
        {envelope.meta.anomalies.map((a) => (
          <AnomalyChip
            key={a.key}
            severity={a.severity === 'critical' ? 'alert' : a.severity}
            reason={a.message}
          />
        ))}
      </footer>
    </>
  );
}

/* ------------------------------------------------------------------ */
/* District data card — appears beside the map.                       */
/* ------------------------------------------------------------------ */

type Totals = {
  events: number;
  reached: number;
  rsvp_yes: number;
  attended: number;
  walk_in: number;
  records: number;
};

interface DistrictCardProps {
  scope: {
    label: string;
    events: number;
    reached: number;
    rsvp_yes: number;
    attended: number;
    walk_in: number;
  };
  totals: Totals;
  isNational: boolean;
  trend: 'accelerating' | 'stable' | 'stalling' | null;
  onOpenFunnel: () => void;
}

const TREND_META: Record<
  NonNullable<DistrictCardProps['trend']>,
  { label: string; color: string }
> = {
  accelerating: { label: 'מאיץ', color: 'var(--positive)' },
  stalling:     { label: 'מאט',  color: 'var(--warning)' },
  stable:       { label: 'יציב', color: 'var(--ink-4)' },
};

function DistrictCard({
  scope,
  totals,
  isNational,
  trend,
  onOpenFunnel,
}: DistrictCardProps) {
  const rows: Array<{ key: string; label: string; value: number; total: number }> = [
    { key: 'events',   label: 'אירועים',       value: scope.events,   total: totals.events   },
    { key: 'reached',  label: 'פניות',         value: scope.reached,  total: totals.reached  },
    { key: 'rsvp_yes', label: 'אישרו הגעה',    value: scope.rsvp_yes, total: totals.rsvp_yes },
    { key: 'attended', label: 'הגיעו בפועל',   value: scope.attended, total: totals.attended },
  ];

  return (
    <div
      style={{
        border: '1px solid var(--hairline)',
        background: 'var(--surface, #fffaf2)',
        borderRadius: 4,
        padding: 16,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8 }}>
        <div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              letterSpacing: '0.08em',
              color: 'var(--ink-3)',
              textTransform: 'uppercase',
            }}
          >
            {isNational ? 'מבט ארצי' : 'מחוז נבחר'}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-sans)',
              fontSize: 22,
              fontWeight: 600,
              color: 'var(--ink)',
              lineHeight: 1.2,
              marginTop: 4,
            }}
          >
            {scope.label}
          </div>
        </div>
        {trend && !isNational && (
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              fontFamily: 'var(--font-sans)',
              fontSize: 11,
              color: 'var(--ink-2)',
              padding: '4px 8px',
              border: '1px solid var(--hairline)',
              borderRadius: 999,
              background: 'var(--paper, #fff)',
              whiteSpace: 'nowrap',
            }}
          >
            <span
              style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                background: TREND_META[trend].color,
              }}
            />
            {TREND_META[trend].label}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {rows.map((r) => {
          const share = r.total > 0 ? r.value / r.total : 0;
          return (
            <div key={r.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'baseline',
                  justifyContent: 'space-between',
                  fontFamily: 'var(--font-sans)',
                  fontSize: 12,
                  color: 'var(--ink-2)',
                }}
              >
                <span>{r.label}</span>
                <span
                  style={{
                    fontFamily: 'var(--font-sans)',
                    fontSize: 14,
                    fontWeight: 600,
                    color: 'var(--ink)',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatNumber(r.value)}
                </span>
              </div>
              <div
                style={{
                  height: 6,
                  background: 'var(--hairline-2, #ece9e2)',
                  borderRadius: 2,
                  overflow: 'hidden',
                  direction: 'ltr',
                }}
                title={
                  isNational
                    ? '100% מהארץ'
                    : `${Math.round(share * 100)}% מהארצי`
                }
              >
                <div
                  style={{
                    height: '100%',
                    width: `${Math.min(100, Math.max(0, share * 100))}%`,
                    background: 'var(--bennett-teal)',
                  }}
                />
              </div>
              {!isNational && (
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 10,
                    color: 'var(--ink-3)',
                  }}
                >
                  {Math.round(share * 100)}% מהארצי
                </div>
              )}
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={onOpenFunnel}
        style={{
          fontFamily: 'var(--font-sans)',
          fontSize: 12,
          padding: '8px 12px',
          background: 'var(--bennett-navy)',
          color: 'var(--paper, #fff)',
          border: '1px solid var(--bennett-navy)',
          borderRadius: 4,
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 6,
          marginTop: 4,
        }}
      >
        ראה משפך מלא
        <Icon name="arrow-left" size={12} />
      </button>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Local presentational helpers — match the prototype's KpiCell.      */
/* ------------------------------------------------------------------ */

interface KpiCellProps {
  label: string;
  value: string;
  sub?: string;
  accent?: boolean;
  focus?: boolean;
}

function KpiCell({ label, value, sub, accent, focus }: KpiCellProps) {
  const cls = ['kpi-cell', accent && 'accent', focus && 'focus']
    .filter(Boolean)
    .join(' ');
  return (
    <div className={cls}>
      <div className="kpi-label">{label}</div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-meta">{sub}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* District leaderboard table — sorted by attended.                   */
/* ------------------------------------------------------------------ */

type DistrictRow = NonNullable<
  ReturnType<typeof useEventsCoverage>['data']
>['data']['districts'][number];

interface DistrictLeaderboardProps {
  rows: DistrictRow[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}

function DistrictLeaderboard({ rows, selectedId, onSelect }: DistrictLeaderboardProps) {
  const sorted = [...rows].sort((a, b) => b.attended - a.attended);
  const maxAttended = Math.max(1, ...rows.map((r) => r.attended));
  const arrowCell: React.CSSProperties = {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    color: 'var(--ink-3)',
    whiteSpace: 'nowrap',
    textAlign: 'right',
  };
  const numHeader: React.CSSProperties = { textAlign: 'right' };
  const trendLabel: Record<DistrictRow['trend'], string> = {
    accelerating: 'מאיץ',
    stalling: 'מאט',
    stable: 'יציב',
  };
  const trendColor: Record<DistrictRow['trend'], string> = {
    accelerating: 'var(--positive)',
    stalling: 'var(--warning)',
    stable: 'var(--ink-4)',
  };

  return (
    <div className="panel">
      <div className="panel-head">
        <div className="title">דירוג מחוזות לפי הגעות</div>
        <div className="sub">לחץ על שורה כדי לסנן את המפה</div>
      </div>
      <table className="table">
        <thead>
          <tr>
            <th>מחוז</th>
            <th style={numHeader}>אירועים</th>
            <th style={numHeader}>פניות</th>
            <th style={numHeader}>אישרו הגעה</th>
            <th style={numHeader}>הגיעו</th>
            <th style={arrowCell}>אישור←הגעה</th>
            <th style={numHeader}>walk-in</th>
            <th>מגמה</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((r) => {
            const yesAtt = Math.max(0, r.attended - r.walk_in);
            const yesShow = r.rsvp_yes > 0 ? yesAtt / r.rsvp_yes : 0;
            const attShare = r.attended / maxAttended;
            const isSelected = selectedId === r.id;
            return (
              <tr
                key={r.id}
                onClick={() => onSelect(r.id)}
                style={{
                  cursor: 'pointer',
                  background: isSelected ? 'var(--surface-2, #f3f1ec)' : undefined,
                  outline: isSelected ? '1px solid var(--ink)' : undefined,
                  outlineOffset: -1,
                }}
              >
                <td>
                  <b>{r.he}</b>
                </td>
                <td className="num-cell">{formatNumber(r.events)}</td>
                <td className="num-cell">{formatNumber(r.reached)}</td>
                <td className="num-cell">{formatNumber(r.rsvp_yes)}</td>
                <td className="num-cell">
                  <div className="cellbar">
                    <div className="track">
                      <div className="fill" style={{ width: `${attShare * 100}%` }} />
                    </div>
                    <div className="v">{formatNumber(r.attended)}</div>
                  </div>
                </td>
                <td className="num-cell" style={arrowCell}>
                  {r.rsvp_yes > 0 ? `${Math.round(yesShow * 100)}%` : '—'}
                </td>
                <td className="num-cell">{formatNumber(r.walk_in)}</td>
                <td>
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      fontFamily: 'var(--font-sans)',
                      fontSize: 11,
                      color: 'var(--ink-2)',
                    }}
                  >
                    <span
                      style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        background: trendColor[r.trend],
                      }}
                    />
                    {trendLabel[r.trend]}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
